import { t } from '@ctcells/i18n'
import { useEffect, useState } from 'react'

import { Button } from '../atoms/Button.tsx'

import type { ControlLockAction, ControlLockView } from './batchStripTypes.ts'

import './batchStrip.css'

function LockIcon({ open }: { open: boolean }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" aria-hidden="true">
      <rect x="5" y="10" width="14" height="10" rx="1.5" fill="currentColor" />
      <path d={open ? 'M8 10V7a4 4 0 0 1 8 0' : 'M8 10V7a4 4 0 0 1 8 0v3'} />
    </svg>
  )
}

export interface ControlLockChipProps {
  lock: ControlLockView
  'data-batch-halt-lock'?: boolean
  /** 타인 보유 락 강제 해제는 `settings` 권한(admin)만 할 수 있다 */
  canForceRelease?: boolean
  /**
   * 획득·반납·강제 해제를 막는 사유. 제어락 조작도 상태 변경이라
   * 스트림이 stale 이면 여기 사유가 들어와 전부 비활성이 된다.
   */
  disabledReason?: string
  onAction?: (action: ControlLockAction) => void
}

/**
 * 제어락 칩 — 와이어프레임 `lockChip()` 1426행 · README §13-1.
 * 상태 변경의 전제라 상시 작업 줄에 붙어 있다(정본 07 §2.2).
 *
 * 4상태: `noperm`(권한 없음) · `none`(획득) · `other`(타인 보유) · `mine`(바로 반납).
 *
 * 🔴 **`mine` 의 남은 시간은 1초마다 줄어든다**(eric 확정 2026-09-21). 값의 근거는 여전히
 *    서버가 준 `expiresAtMs` 뿐이고(`useLockCountdownText`), **표시용 시계**다 — 0 이 됐다고
 *    이 컴포넌트가 상태를 바꾸지 않는다. 상태 전이(`mine` → `expired`)는 종전 그대로 하트비트
 *    성공(되감김) · 실패 · 409 로만 일어난다(리스 보관소 · DP-04). `other` 의 `remainingText` 는
 *    서버 표기를 그대로 쓰는 종전 규칙 그대로다(남의 락 잔여는 계약에 없어 대개 비어 있다).
 *
 * ⚠ 와이어프레임의 반납 드롭다운(§13-1)은 없앴다(eric 2026-10-29) — [반납]은 한 번에 나간다. 되살리지 마라.
 */
function formatRemaining(ms: number): string {
  const totalSeconds = Math.max(0, Math.ceil(ms / 1000))
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return `${minutes}:${String(seconds).padStart(2, '0')}`
}

/**
 * `mine` 락의 남은 시간을 1초마다 다시 그린다. **표시 전용** — 상태 판정에는 안 쓴다.
 * 하트비트가 성공해 `expiresAtMs` 가 갱신되면(부모가 새 값을 넘기면) 여기서 다시 그 값부터
 * 세기 시작한다 — 정상일 때 30 → 20 → 30 → 20 … 으로 되감기는 게 「살아 있다」는 신호다.
 */
function useLockCountdownText(
  expiresAtMs: number | undefined,
  receivedAtMs: number | undefined,
): string | undefined {
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    if (expiresAtMs === undefined) return
    const id = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(id)
  }, [expiresAtMs])

  /*
   * 🔴 «지금»은 받은 시각보다 이를 수 없다 — 갱신이 온 그 렌더의 `now` 는 직전 틱(최대 1초 전)이라, 그대로 빼면
   *    「20 → 31」로 섰다(eric 2026-10-14). 받은 시각은 서버 값이 아니라 이 기기가 응답을 받은 시각이다.
   */
  const at = Math.max(now, receivedAtMs ?? 0)
  return expiresAtMs === undefined ? undefined : formatRemaining(expiresAtMs - at)
}
export function ControlLockChip({
  lock,
  'data-batch-halt-lock': batchHaltLock,
  canForceRelease = false,
  disabledReason,
  onAction,
}: ControlLockChipProps) {
  /** 사유가 있으면 버튼을 숨기지 않고 비활성 + 사유로 남긴다(§5-6) */
  const gate = (action: ControlLockAction) =>
    disabledReason === undefined
      ? ({ disabled: false, onClick: () => onAction?.(action) } as const)
      : ({ disabled: true, disabledReason } as const)

  // 조건부 이른 반환보다 위에 둔다 — 훅은 매 렌더 같은 순서로 불러야 한다(rules-of-hooks)
  const countdownText = useLockCountdownText(
    lock.kind === 'mine' ? lock.expiresAtMs : undefined,
    lock.kind === 'mine' ? lock.leaseReceivedAtMs : undefined,
  )

  if (lock.kind === 'noperm') {
    return (
      <span data-batch-halt-lock={batchHaltLock} className="lock lock--noperm">
        <LockIcon open />
        {t('lock.noPermission')}
      </span>
    )
  }

  if (lock.kind === 'unknown') {
    return (
      <span data-batch-halt-lock={batchHaltLock} className="lock lock--none">
        <LockIcon open />
        {t('lock.unknown')}
        <Button size="pill" disabled disabledReason={t('lock.unknown')}>
          {t('action.acquireLock')}
        </Button>
      </span>
    )
  }

  if (lock.kind === 'none') {
    return (
      <span data-batch-halt-lock={batchHaltLock} className="lock lock--none">
        <LockIcon open />
        {t('lock.none')}
        <Button size="pill" variant="primary" {...gate('acquire')}>
          {t('action.acquireLock')}
        </Button>
      </span>
    )
  }

  /**
   * 🔴 **`none` 과 다른 말을 한다.** 「아직 안 잡았다」가 아니라 **「쥐고 있었는데 끊겼다」**이고,
   *    기다린다고 돌아오지 않는다 — 그래서 버튼이 「다시 획득」이다(경로는 같은 `acquire`).
   *    둘을 한 얼굴로 합치면 사용자가 「왜 갑자기 버튼이 다 꺼졌나」를 알 길이 없다.
   */
  if (lock.kind === 'expired') {
    return (
      <span data-batch-halt-lock={batchHaltLock} className="lock lock--expired">
        <LockIcon open />
        {t('lock.expired')}
        <Button size="pill" variant="primary" {...gate('acquire')}>
          {t('action.reacquireLock')}
        </Button>
      </span>
    )
  }

  if (lock.kind === 'other') {
    return (
      <span data-batch-halt-lock={batchHaltLock} className="lock lock--other">
        <LockIcon open={false} />
        {lock.holder}
        {lock.remainingText ? <small className="num">{lock.remainingText}</small> : null}
        {canForceRelease ? (
          <Button size="pill" {...gate('force')}>
            {t('action.forceReleaseLock')}
          </Button>
        ) : null}
      </span>
    )
  }

  return (
    <span data-batch-halt-lock={batchHaltLock} className="lock lock--mine">
      <LockIcon open={false} />
      {lock.holder}
      {/*
       * 🔴 1초마다 글자가 바뀐다 — 스크린리더가 매초 읊지 않게 `aria-hidden`.
       *    「내가 보유」(위 `{lock.holder}`)만 읽히면 충분하다. 색이 아니라 숫자 자체로 알린다.
       */}
      {countdownText !== undefined ? (
        <small className="num" aria-hidden="true">
          {countdownText}
        </small>
      ) : null}
      {/*
       * 🔴 **한 번 누르면 바로 반납한다**(eric 2026-10-29 「반납 누르면.. 그냥 반납하면 안 돼? 안 물어보고?」).
       *    메뉴도 확인 창도 없다 — 남은 시간은 칩에 이미 보인다. 사유는 컨테이너가 고정 문구로 싣는다(계약 필수값).
       *    ⚠ 남의 락 [강제 해제]는 확인 창 그대로다(남의 작업을 끊는다).
       */}
      <Button size="pill" {...gate('release')}>
        {t('action.releaseLock')}
      </Button>
    </span>
  )
}
